import { FC, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Loader } from 'shared/elements/Loader';
import { LazyWithRetry } from 'shared/utils/LazyWithRetry';
import css from './App.module.css';
import AttackMatrix from './AttackMatrix/AttackMatrixContainer';
import { Nav } from './Nav';
import FreeTrial from './FreeTrial';
import EULA from './EULA';
import Security from './Security';
import { useUserContext } from 'shared/user/UserContext';
import { config } from 'core/config';

const NotFound = LazyWithRetry(() => import('./NotFound'));
const Dashboard = LazyWithRetry(() => import('./Dashboard'));
const Settings = LazyWithRetry(() => import('./Settings'));
const Techniques = LazyWithRetry(() => import('./Techniques'));
const TechniquesDetail = LazyWithRetry(
  () => import('./Techniques/TechniquesDetail')
);
const Assets = LazyWithRetry(() => import('./Assets'));
const AssetsDetail = LazyWithRetry(() => import('./Assets/AssetsDetail'));
const ThreatGroups = LazyWithRetry(() => import('./ThreatGroups'));
const ThreatGroupsDetail = LazyWithRetry(
  () => import('./ThreatGroups/ThreatGroupsDetail')
);
const Campaigns = LazyWithRetry(() => import('./Campaigns'));
const CampaignsDetail = LazyWithRetry(
  () => import('./Campaigns/CampaignsDetail')
);
const Softwares = LazyWithRetry(() => import('./Softwares'));
const SoftwareDetail = LazyWithRetry(
  () => import('./Softwares/SoftwareDetail')
);
const Visibility = LazyWithRetry(() => import('./Visibility'));
const VisibilityDetail = LazyWithRetry(
  () => import('./Visibility/VisibilityDetail')
);
const Detections = LazyWithRetry(() => import('./Detections'));
const DetectionsDetail = LazyWithRetry(
  () => import('./Detections/DetectionsDetail')
);
const Identities = LazyWithRetry(() => import('./Identities/Identities'));
const IdentityDetail = LazyWithRetry(
  () => import('./Identities/detail/IdentityDetailContainer')
);
const ThreatProfiles = LazyWithRetry(() => import('./Settings/ThreatProfiles'));
const Integrations = LazyWithRetry(() => import('./Settings/Integrations'));
const IntegrationDetail = LazyWithRetry(
  () => import('./Settings/Integrations/IntegrationDetail')
);
const Vulnerability = LazyWithRetry(() => import('./Vulnerability'));
const VulnerabilityDetail = LazyWithRetry(
  () => import('./Vulnerability/VulnerabilityDetail')
);
const Controls = LazyWithRetry(() => import('./Controls'));
const ControlsDetail = LazyWithRetry(() => import('./Controls/ControlsDetail'));
const ProductComparison = LazyWithRetry(() => import('./ProductComparison'));
const Logout = LazyWithRetry(() => import('./Settings/Logout'));
const AIChatConversation = LazyWithRetry(
  () => import('./AIChat/AIChatConversation')
);
const AIChatWelcome = LazyWithRetry(() => import('./AIChat/AIChatWelcome'));
const FreeTrialPagePath = '/free-trial';
const EULAPagePath = '/terms';
const SecurityPagePath = '/security';

export const App: FC = () => {
  const { isAuthenticated } = useUserContext();
  const location = useLocation();
  const currentPath = location.pathname;

  return (
    <div className={css.app}>
      {isAuthenticated &&
        currentPath !== FreeTrialPagePath &&
        currentPath !== EULAPagePath &&
        currentPath !== SecurityPagePath && <Nav />}

      <div className={css.content}>
        <Suspense fallback={<Loader />}>
          {/* This could really go anywhere */}
          <Routes>
            {/* Public Routes */}
            {!config.isOnPremise && (
              <Route path="/free-trial" element={<FreeTrial />} />
            )}
            <Route path="/terms" element={<EULA />} />
            <Route path="/security" element={<Security />} />

            {/* Dashboard (Root) */}
            <Route path="ciso" element={<Dashboard />} />

            {/* Threat Exposure */}
            <Route path="campaigns" element={<Campaigns />} />
            <Route path="campaigns/:id" element={<CampaignsDetail />} />
            <Route path="techniques/:id" element={<TechniquesDetail />} />
            <Route path="techniques" element={<Techniques />} />
            <Route path="software/*" element={<Softwares />} />
            <Route path="software/:id" element={<SoftwareDetail />} />
            <Route path="threat-groups/*" element={<ThreatGroups />} />
            <Route path="/attack-matrix" element={<AttackMatrix />} />
            <Route path="threat-groups/:id" element={<ThreatGroupsDetail />} />
            <Route path="threat-profiles/:tab?" element={<ThreatProfiles />} />

            {/* Defense Surface */}
            <Route path="visibility/:id" element={<VisibilityDetail />} />
            <Route path="visibility" element={<Visibility />} />
            <Route path="detections/*" element={<Detections />} />
            <Route path="detections/:id" element={<DetectionsDetail />} />
            <Route path="identities/*" element={<Identities />} />
            <Route path="identities/:id" element={<IdentityDetail />} />
            <Route path="integrations/:tab?" element={<Integrations />} />
            <Route
              path="integration/:id/:tab?"
              element={<IntegrationDetail />}
            />
            <Route path="product-comparison" element={<ProductComparison />} />

            {/* Fleet Exposure */}
            <Route
              path="recommendations/vulnerabilities/:id"
              element={<VulnerabilityDetail />}
            />
            <Route path="vulnerabilities/*" element={<Vulnerability />} />
            <Route
              path="vulnerabilities/:id"
              element={<VulnerabilityDetail />}
            />
            <Route path="asset/:id" element={<AssetsDetail />} />
            <Route path="asset" element={<Assets />} />

            {/* AI Chat */}
            <Route path="aichat/welcome" element={<AIChatWelcome />} />
            <Route
              path="aichat/conversation"
              element={<AIChatConversation />}
            />

            {/* Settings */}
            <Route path="settings/*" element={<Settings />} />

            {/* Redirects */}
            <Route path="/" element={<Navigate to="ciso" replace />} />

            {/* Special Pages */}
            <Route path="/auth/logout" element={<Logout />} />
            <Route path="404" element={<NotFound />} />
            <Route path="*" element={<Navigate to="/404" replace />} />

            {/* Nested */}
            <Route
              path="recommendations/visibility/:id"
              element={<VisibilityDetail />}
            />
            <Route
              path="recommendations/techniques/:id"
              element={<TechniquesDetail />}
            />
            <Route
              path="recommendations/software/:id"
              element={<SoftwareDetail />}
            />
            <Route
              path="recommendations/threat-groups/:id"
              element={<ThreatGroupsDetail />}
            />
            <Route
              path="recommendations/controls/:id"
              element={<ControlsDetail />}
            />
            <Route path="controls/*" element={<Controls />} />
            <Route path="controls/:id" element={<ControlsDetail />} />
          </Routes>
        </Suspense>
      </div>
    </div>
  );
};
